import React from 'react';
import Content from 'gatsby-theme-core/src/components/content';
import { StaticImage } from 'gatsby-plugin-image';
import Factory from 'icons/src/Factory';
import GasPump from 'icons/src/GasPump2';
import Paintbrush from 'icons/src/Paintbrush';
import User from 'icons/src/User';
import Odometer from 'icons/src/Odometer';
import Gear from 'icons/src/Gear';
import Percentage from 'icons/src/Percentage';
import LicensePlate from 'icons/src/LicensePlate';
import Transmission from 'icons/src/Transmission';
import Bolt from 'icons/src/Bolt';
import priceFormat from '../helpers/price-format';
import { deEnum as deEnumFuel, shorten as shortenFuel } from '../helpers/rewriteFuel';
import { deEnum as deEnumTransmission, shorten as shortenTransmission } from '../helpers/rewriteTransmission';
import rewriteOdometerUnit from '../helpers/rewriteOdometerUnit';
import EnergyLabel from './energy-label';

function capitalizeFirstLetter(string) {
  if (!string) {
    return null;
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function booleanToJaNee(bool) {
  if (bool === true) {
    return 'Ja';
  }
  if (bool === false) {
    return 'Nee';
  }
  return null;
}

const specsMapping = {
  __typename: () => null,
  id: () => null,
  strapiId: () => null,
  documentId: () => null,
  lowestPrice: ({ lowestPrice }) => ({
    label: 'Prijs',
    value: lowestPrice ? priceFormat(lowestPrice) : null,
  }),
  vin: () => ({
    label: 'Chassisnummer',
  }),
  location: () => ({
    label: 'Locatie',
  }),
  licensePlate: () => ({
    label: 'Nummerplaat',
    icon: LicensePlate,
  }),
  buildyear: () => ({
    label: 'Bouwjaar',
    icon: Factory,
  }),
  body: () => ({
    label: 'Carrosserie',
  }),
  vehicleType: () => ({
    label: 'Voertuigsoort',
  }),
  make: () => ({
    label: 'Merk',
  }),
  model: () => ({
    label: 'Model',
  }),
  type: () => ({
    label: 'Type',
  }),
  typeAddition: () => ({
    label: 'Type toevoeging',
  }),
  odometer: ({ odometer, odometerUnit = 'Kilometers', napWeblabel }) => ({
    label: 'Tellerstand',
    value: odometer ? <>{`${odometer.toLocaleString('nl-NL')} ${rewriteOdometerUnit(odometerUnit)}`} {napWeblabel && <StaticImage width={48} src="./nap.png" alt="NAP" />}</> : null,
    shortValue: odometer ? odometer.toLocaleString('nl-NL') : null,
    icon: Odometer,
  }),
  odometerUnit: () => null,
  fuel: ({ fuel }) => ({
    label: 'Brandstof',
    value: deEnumFuel(fuel),
    shortValue: shortenFuel(fuel),
    icon: GasPump,
  }),
  transmission: ({ transmission }) => ({
    label: 'Transmissie',
    icon: Transmission,
    value: deEnumTransmission(transmission),
    shortValue: shortenTransmission(transmission),
  }),
  gears: () => ({
    label: 'Versnellingen',
    icon: Gear,
  }),
  seats: () => ({
    label: 'Zitplaatsen',
    icon: User,
  }),
  doors: () => ({
    label: 'Deuren',
  }),
  keys: () => ({
    label: 'Sleutels',
  }),
  transmitters: () => ({
    label: 'Transmitters',
  }),
  previousOwners: () => ({
    label: 'Eerdere eigenaren',
  }),
  taxType: () => ({
    label: 'BTW/Marge',
    icon: Percentage,
  }),
  isTaxVehicle: ({ taxType, regularPriceExBtw }) => ({
    label: 'BTW auto',
    value: taxType === 'BTW' ? `ja, ${regularPriceExBtw ? 'excl.' : 'incl.'} BTW` : 'nee, marge auto',
  }),
  maintenanceLogs: ({ maintenanceLogs }) => ({
    label: 'Boekjes',
    value: capitalizeFirstLetter(maintenanceLogs),
  }),
  baseColor: ({ baseColor }) => ({
    key: 'baseColor',
    label: 'Kleur',
    icon: Paintbrush,
    value: capitalizeFirstLetter(baseColor),
  }),
  color: ({ color }) => ({
    key: 'color',
    label: 'Kleur',
    icon: Paintbrush,
    value: capitalizeFirstLetter(color),
  }),
  typeOfPaint: ({ typeOfPaint }) => ({
    label: 'Laksoort',
    value: capitalizeFirstLetter(typeOfPaint),
  }),
  interiorColor: () => ({
    label: 'Interieurkleur',
  }),
  upholstery: ({ upholstery }) => {
    let value;
    switch (upholstery) {
      case 'Half_leder_alcantara':
        value = 'Half leder / alcantara';
        break;
      case 'Half_leder_stof':
        value = 'Half leder / stof';
        break;
      default:
        value = upholstery;
    }
    return {
      label: 'Bekleding',
      value,
    };
  },
  datePart1: () => ({
    label: 'Afgifte datum deel 1',
  }),
  datePart1a: () => ({
    label: 'Afgifte datum deel 1a',
  }),
  datePart1b: () => ({
    label: 'Afgifte datum deel 1b',
  }),
  apkUntil: () => ({
    label: 'APK tot',
  }),
  apkAtDelivery: ({ apkAtDelivery }) => ({
    label: 'APK bij aflevering',
    value: booleanToJaNee(apkAtDelivery),
  }),
  weight: (specs) => ({
    label: 'Gewicht',
    value: `${specs.weight} kg`,
  }),
  maxTowing: ({ maxTowing }) => ({
    label: 'Max trekgewicht',
    value: `${maxTowing} kg`,
  }),
  payload: () => ({
    label: 'Laadvermogen',
  }),
  cylinders: () => ({
    label: 'Cilinders',
  }),
  cylindersCapacity: ({ cylindersCapacity }) => ({
    label: 'Cilinderinhoud',
    value: `${cylindersCapacity} cc`,
  }),
  powerKw: ({ powerKw }) => ({
    key: 'powerKw',
    label: 'Vermogen',
    value: `${powerKw} kw`,
  }),
  powerHp: ({ powerHp }) => ({
    key: 'powerHp',
    label: 'Vermogen',
    value: `${powerHp} pk`,
    icon: Bolt,
  }),
  vehicleLength: () => ({
    label: 'Lengte',
  }),
  width: () => ({
    label: 'Breedte',
  }),
  height: () => ({
    label: 'Hoogte',
  }),
  emissionClass: () => ({
    label: 'Emissieklasse',
  }),
  energylabel: ({ energylabel }) => ({
    label: 'Energielabel',
    value: <EnergyLabel label={energylabel} />,
  }),
  pluginHybrid: ({ pluginHybrid }) => ({
    label: 'Plug-in hybrid',
    value: booleanToJaNee(pluginHybrid),
  }),
  e10Capable: ({ e10Capable }) => ({
    label: 'E10 geschikt',
    value: booleanToJaNee(e10Capable),
  }),
  dateOfArrival: () => ({
    label: 'Datum binnenkomst',
  }),
  dateOfSale: () => ({
    label: 'Verkoopdatum',
  }),
  topspeed: ({ topspeed }) => ({
    label: 'Topsnelheid',
    value: `${topspeed} km/u`,
  }),
  tankCapacity: () => ({
    label: 'Tank inhoud',
  }),
  range: ({ range }) => ({
    label: 'Actieradius',
    value: `${range} km`,
  }),
  electricRange: ({ electricRange }) => ({
    label: 'Actieradius elektrisch',
    value: `${electricRange} km`,
  }),
  acceleration: ({ acceleration }) => ({
    label: 'Acceleratie (0-100km)',
    value: `${acceleration} s`,
  }),
  consumptionCity: ({ consumptionCity }) => ({
    label: 'Gemiddeld stad',
    value: `${consumptionCity} l/100km`,
  }),
  consumptionHighway: ({ consumptionHighway }) => ({
    label: 'Gemiddeld snelweg',
    value: `${consumptionHighway} l/100km`,
  }),
  averageConsumption: ({ averageConsumption }) => ({
    label: 'Gemiddeld verbruik',
    value: `${averageConsumption} l/100km`,
  }),
  co2Emissions: ({ co2Emissions }) => ({
    label: 'C02 uitstoot',
    value: `${co2Emissions} g/km`,
  }),
  particulateMatterEmissions: () => ({
    label: 'Fijnstof uitstoot', // @TODO: value?
  }),
  napWeblabel: ({ napWeblabel }) => ({
    label: 'NAP Weblabel',
    value: booleanToJaNee(napWeblabel),
  }),
  honderdProcentOnderhouden: ({ honderdProcentOnderhouden }) => ({
    label: 'Honderd Procent Onderhouden label',
    value: booleanToJaNee(honderdProcentOnderhouden),
  }),
  bovag40puntencheck: ({ bovag40puntencheck }) => ({
    label: 'BOVAG 40 punten check',
    value: booleanToJaNee(bovag40puntencheck),
  }),
  warrantyMonths: () => ({
    label: 'Garantie maanden',
  }),
  warrantyComments: ({ warrantyComments }) => {
    if (!warrantyComments?.data?.childMarkdownRemark?.html) {
      return null;
    }
    return {
      label: 'Garantie opmerkingen',
      value: <Content asSpan $content={warrantyComments} />,
    };
  },
  environmentalBadgeDe: ({ environmentalBadgeDe }) => ({
    label: 'Milieusticker Duitsland',
    value: capitalizeFirstLetter(environmentalBadgeDe),
  }),
  environmentalBadgeFr: ({ environmentalBadgeFr }) => ({
    label: 'Milieusticker Frankrijk',
    value: capitalizeFirstLetter(environmentalBadgeFr),
  }),
  quarterlyRoadTaxesMin: (specs) => {
    if (!specs.quarterlyRoadTaxesMax) {
      return null;
    }
    return {
      label: 'Motorrijtuigen belasting',
      value: `€ ${specs.quarterlyRoadTaxesMin} - ${specs.quarterlyRoadTaxesMax} per kwartaal`,
    };
  },
  quarterlyRoadTaxesMax: () => null,
  // this assumes you passed leases?.[0]?.maandbedrag as `lease`
  lease: ({ lease }) => ({
    label: 'Privé lease voor',
    value: lease ? `${priceFormat(lease)} p.m.` : null,
  }),
  isSold: ({ isSold }) => ({
    label: 'Beschikbaarheid',
    value: isSold ? 'Verkocht' : 'Beschikbaar',
  }),
};

const mapSpecs = (data, {
  useShortValues = [],
  labels = {},
  ignore = [],
} = {}) => Object.entries(data).reduce((acc, [key, originalValue]) => {
  if (ignore.includes(key)) {
    // if we need data for other fields but dont want to show itself
    // f.e. taxType when using isTaxVehicle
    return acc;
  }
  if (originalValue === null) {
    return acc;
  }
  const fn = specsMapping[key];
  const label = labels[key];
  if (!fn) {
    return acc.concat({
      label: label || key,
      value: originalValue,
    });
  }
  const spec = fn(data);
  if (spec === null) {
    return acc;
  }

  if (label) {
    // allow manual setting of the label via the labels param
    spec.label = label;
  }

  if (spec.shortValue && useShortValues.includes(key)) {
    return acc.concat({
      ...spec,
      value: spec.shortValue,
    });
  }

  return acc.concat({
    ...spec,
    value: spec.value ? spec.value : originalValue,
  });
}, []);

export default mapSpecs;
